import React, { useContext, useEffect, useMemo, useState } from 'react'
import style from './layout.mod.scss'
import PropTypes from 'prop-types'
import { Button } from '@components'
import { HelmetDatoCms } from 'gatsby-source-datocms'
import { useStaticQuery, graphql } from 'gatsby'
import Headroom from 'react-headroom'
import { useLocation } from '@reach/router'
import { useQuerySubscription } from 'react-datocms'
import {
  Header,
  Footer,
  Drilldown,
  Search,
  SearchModal,
  Popup
} from '@components'
import ThemeContext, { isBrowser } from '@context/themeContext'
import SearchContext from '@context/searchContext'

const normalizePath = path => {
  if (!path) return '/'
  if (path === '/') return '/'
  return path.replace(/\/+$/, '')
}

const resolveLinkPath = link => {
  if (!link) return null

  if (link.path) {
    return link.path
  }

  if (link.link && link.link.path) {
    return link.link.path
  }

  if (link.url) {
    return link.url
  }

  return null
}

// Google requires the Organization logo to be a raster image of at least 112x112px.
// The CMS field accepts SVG, so pass it through the DatoCMS imgix pipeline, which
// rasterises to PNG whenever a resize param is applied. Height is constrained rather
// than width because logos are wider than they are tall, making height the dimension
// that would otherwise fall under the 112px floor.
// Keep this to a single query param: JSON-LD string values are HTML-escaped on the
// way into the <script>, which would turn an "&" separator into "&amp;".
const toSchemaLogoUrl = url => (url ? `${url}?h=200` : null)

// react-schemaorg's <JsonLd> escapes &, <, >, " and ' in every string value. Inside a
// <script> those entities are never decoded, so consumers read them literally. Emit the
// script directly instead, escaping only "<" so a value can't break out of the element.
const toJsonLdHtml = item =>
  JSON.stringify(item, (_, value) =>
    value === null ? undefined : value
  ).replace(/</g, '\\u003c')

const POPUP_VIEW_STORAGE_KEY = 'layout.popupLastViewed'
const POPUP_NEVER_SHOW_STORAGE_KEY = 'layout.popupNeverShow'
const POPUP_SUPPRESSION_MS = 24 * 60 * 60 * 1000

const parseTimestamp = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const pruneExpiredEntries = (entries, now) => {
  if (!entries || typeof entries !== 'object') {
    return {}
  }

  return Object.entries(entries).reduce((acc, [id, rawTimestamp]) => {
    const timestamp = parseTimestamp(rawTimestamp)

    if (!timestamp) {
      return acc
    }

    if (now - timestamp < POPUP_SUPPRESSION_MS) {
      acc[id] = timestamp
    }

    return acc
  }, {})
}

const readPopupViewStorage = () => {
  if (!isBrowser()) {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(POPUP_VIEW_STORAGE_KEY)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw)
    return pruneExpiredEntries(parsed, Date.now())
  } catch (error) {
    return {}
  }
}

const persistPopupViewStorage = entries => {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(
      POPUP_VIEW_STORAGE_KEY,
      JSON.stringify(entries || {})
    )
  } catch (error) {
    // Silently fail if storage is unavailable
  }
}

const normalizeNeverShowEntries = entries => {
  if (!entries) {
    return {}
  }

  if (Array.isArray(entries)) {
    return entries.reduce((acc, id) => {
      if (typeof id === 'string' && id.trim().length > 0) {
        acc[id] = true
      }
      return acc
    }, {})
  }

  if (typeof entries === 'object') {
    return Object.entries(entries).reduce((acc, [id, value]) => {
      if (typeof id === 'string' && value) {
        acc[id] = true
      }
      return acc
    }, {})
  }

  return {}
}

const readNeverShowStorage = () => {
  if (!isBrowser()) {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(POPUP_NEVER_SHOW_STORAGE_KEY)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw)
    return normalizeNeverShowEntries(parsed)
  } catch (error) {
    return {}
  }
}

const persistNeverShowStorage = entries => {
  if (!isBrowser()) {
    return
  }

  try {
    window.localStorage.setItem(
      POPUP_NEVER_SHOW_STORAGE_KEY,
      JSON.stringify(entries || {})
    )
  } catch (error) {
    // Silently fail if storage is unavailable
  }
}

const Layout = ({ children, pageType = '', isWecareNowPage }) => {
  const {
    site,
    allSitePage,
    popups,
    globalCtas,
    globalOrg
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          lang
          siteUrl
        }
      }

      globalCtas: datoCmsGlobal {
        callToActionButtons {
          id
          buttonStyle
          icon
          link {
            ...Link
          }
        }
        showManageMyHealth
        manageMyHealthLink {
          ...Link
        }
      }

      globalOrg: datoCmsGlobal {
        orgName
        legalName
        nzbn
        orgDescription
        orgPhone
        footerEmail
        facebook
        instagram
        linkedin
        orgLogo {
          url
        }
      }

      allSitePage {
        nodes {
          ...MenuHelpersAllPages
        }
      }

      popups: allDatoCmsPopup {
        nodes {
          id
          heading
          content
          newTag
          visibleOnAllPages
          headingBackgroundColour {
            id
            scheme
          }
          buttons {
            id
            buttonColour {
              id
              scheme
            }
            link {
              ...Link
            }
          }
          pagesForPopupToAppear {
            ... on DatoCmsHome {
              id
              path
            }
            ... on DatoCmsPage {
              id
              path
            }
          }
        }
      }
    }
  `)

  const location = useLocation()
  const { isSidebarOpen, closeSidebar } = useContext(ThemeContext)
  const { isSearchModalOpen, setIsSearchModalOpen } = useContext(SearchContext)
  const [dismissedPopupIds, setDismissedPopupIds] = useState([])
  const [recentlyViewedPopups, setRecentlyViewedPopups] = useState({})
  const [neverShowPopupIds, setNeverShowPopupIds] = useState({})
  const [currentPopupId, setCurrentPopupId] = useState(null)

  // Detect admin preview mode via ?previewpopup={recordID}
  const previewPopupId = useMemo(() => {
    try {
      const search = location?.search || ''
      const params = new URLSearchParams(search)
      const id = params.get('previewpopup')
      return id && id.trim().length > 0 ? id.trim() : null
    } catch (e) {
      return null
    }
  }, [location?.search])

  // Select popup directly from all popups, bypassing live gating and suppression
  const previewPopup = useMemo(() => {
    if (!previewPopupId) return null
    const nodes = popups?.nodes || []
    // Dato IDs sometimes differ by prefix/suffix; perform loose includes match both ways
    const match = nodes.filter(Boolean).find(node => {
      const nid = typeof node?.id === 'string' ? node.id : ''
      return (
        nid === previewPopupId ||
        nid.includes(previewPopupId) ||
        previewPopupId.includes(nid)
      )
    })
    return match || null
  }, [popups, previewPopupId])

  const { data: livePopupData } = useQuerySubscription({
    enabled: true,
    query: `
      query LivePopups {
        allPopups {
          id
        }
      }
    `,
    token: 'c7f8c20e9e3b736f66e1e097fbb9a2',
    preview: false,
    environment: 'main'
  })

  // Fetch live popup IDs so we only render currently published popups
  const livePopupIds = useMemo(() => {
    if (!livePopupData || typeof livePopupData.allPopups === 'undefined') {
      return null
    }

    if (!Array.isArray(livePopupData.allPopups)) {
      return []
    }

    return livePopupData.allPopups.map(popup => popup?.id).filter(Boolean)
  }, [livePopupData])

  useEffect(() => {
    closeSidebar()
  }, [closeSidebar])

  useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    const stored = readPopupViewStorage()
    const storedNeverShow = readNeverShowStorage()
    setRecentlyViewedPopups(stored)
    setNeverShowPopupIds(storedNeverShow)
    return undefined
  }, [])

  useEffect(() => {
    if (!isBrowser()) {
      return undefined
    }

    const now = Date.now()
    const pruned = pruneExpiredEntries(recentlyViewedPopups, now)

    if (
      Object.keys(pruned).length !== Object.keys(recentlyViewedPopups).length
    ) {
      setRecentlyViewedPopups(pruned)
      persistPopupViewStorage(pruned)
    }

    return undefined
  }, [recentlyViewedPopups])

  const popupsData = useMemo(() => {
    const nodes = popups?.nodes || []

    // Do not show any popup until live IDs are loaded
    if (livePopupIds === null) {
      return []
    }

    if (!livePopupIds.length) {
      return []
    }

    const now = Date.now()

    return nodes
      .filter(popup => {
        if (!popup?.id || typeof popup.id !== 'string') {
          return false
        }

        if (neverShowPopupIds?.[popup.id] && popup.id !== currentPopupId) {
          return false
        }

        return livePopupIds.some(liveId => {
          if (!liveId || typeof liveId !== 'string') {
            return false
          }

          return popup.id.includes(liveId) || liveId.includes(popup.id)
        })
      })
      .filter(popup => {
        const viewedAt = parseTimestamp(recentlyViewedPopups?.[popup.id])

        if (!viewedAt) {
          return true
        }

        if (popup.id === currentPopupId) {
          return true
        }

        return now - viewedAt >= POPUP_SUPPRESSION_MS
      })
  }, [
    currentPopupId,
    livePopupIds,
    neverShowPopupIds,
    popups,
    recentlyViewedPopups
  ])

  const activePopup = useMemo(() => {
    if (!popupsData.length) return null

    const currentPath = normalizePath(location?.pathname)

    const candidates = popupsData
      .filter(popup => popup && !dismissedPopupIds.includes(popup.id))
      .filter(popup => {
        if (popup.visibleOnAllPages) {
          return true
        }

        const allowedPaths = (popup.pagesForPopupToAppear || [])
          .map(resolveLinkPath)
          .filter(Boolean)
          .map(normalizePath)

        if (!allowedPaths.length) {
          return false
        }

        return allowedPaths.includes(currentPath)
      })
      .sort((a, b) => a.id.localeCompare(b.id))

    return candidates[0] || null
  }, [dismissedPopupIds, location?.pathname, popupsData])

  useEffect(() => {
    // In preview mode, skip tracking and side effects entirely
    if (previewPopup) {
      setCurrentPopupId(previewPopup.id || null)
      return undefined
    }

    if (!activePopup?.id) {
      setCurrentPopupId(null)
      return undefined
    }

    setCurrentPopupId(activePopup.id)

    if (!isBrowser()) {
      return undefined
    }

    setRecentlyViewedPopups(prev => {
      const now = Date.now()
      const pruned = pruneExpiredEntries(prev, now)
      const updated = {
        ...pruned,
        [activePopup.id]: now
      }
      persistPopupViewStorage(updated)
      return updated
    })

    return undefined
  }, [activePopup?.id, previewPopup])

  const handlePopupDismiss = popupId => {
    if (!popupId) return

    setDismissedPopupIds(prev => {
      if (prev.includes(popupId)) {
        return prev
      }

      return [...prev, popupId]
    })
  }

  const handlePopupNeverShow = popupId => {
    if (!popupId) return

    setNeverShowPopupIds(prev => {
      const safePrev = prev || {}

      if (safePrev[popupId]) {
        return safePrev
      }

      const updated = {
        ...safePrev,
        [popupId]: true
      }

      persistNeverShowStorage(updated)
      return updated
    })

    setRecentlyViewedPopups(prev => {
      if (!prev || typeof prev !== 'object' || !prev[popupId]) {
        return prev
      }

      const next = { ...prev }
      delete next[popupId]
      persistPopupViewStorage(next)
      return next
    })
  }

  const schema = useMemo(() => {
    const {
      orgName,
      legalName,
      nzbn,
      orgDescription,
      orgPhone,
      footerEmail,
      facebook,
      instagram,
      linkedin,
      orgLogo
    } = globalOrg || {}

    const { siteUrl } = site.siteMetadata
    const sameAs = [facebook, instagram, linkedin].filter(Boolean)

    return {
      '@context': 'https://schema.org',
      '@type': 'MedicalOrganization',
      '@id': `${siteUrl}/#organization`,
      name: orgName,
      legalName,
      ...(nzbn && {
        identifier: {
          '@type': 'PropertyValue',
          propertyID: 'NZBN',
          value: nzbn
        }
      }),
      description: orgDescription,
      url: `${siteUrl}/`,
      ...(orgLogo?.url && { logo: toSchemaLogoUrl(orgLogo.url) }),
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: orgPhone,
        email: footerEmail
      },
      ...(sameAs.length > 0 && { sameAs })
    }
  }, [globalOrg, site.siteMetadata])

  const menuTreeFromPages = nodes => {
    const allMenuItems = nodes
      .filter(node => node.pageContext !== null && node.pageContext.showPage)
      .map(node => {
        return { path: node.path, ...node.pageContext }
      })

    const datoPages = allMenuItems
      .filter(item => item.hasParent === false && item.type === 'DatoCmsPage')
      .sort((a, b) => a.position - b.position)

    datoPages.forEach(parent => {
      const { treeChildren } = parent
      if (treeChildren.length > 0) {
        parent.treeChildren = treeChildren
          .filter(child => child.showPage)
          .map(child => {
            return {
              ...child
            }
          })
          .sort((a, b) => a.position - b.position)
      }
    })
    return [...datoPages]
  }

  const wecareNowMenuTreeFromPages = nodes => {
    const menuitems =
      nodes.filter(node => node.path === '/wecare-now/')[0].pageContext
        .treeChildren || []

    const datoPages = menuitems
      .filter(
        item =>
          item?.treeParent !== null &&
          item?.internal?.type === 'DatoCmsPage' &&
          item.showPage
      )
      .sort((a, b) => a.position - b.position)

    datoPages.forEach(parent => {
      const { treeChildren } = parent
      if (treeChildren.length > 0) {
        parent.treeChildren = treeChildren
          .filter(child => child.showPage)
          .map(child => {
            return {
              ...child
            }
          })
          .sort((a, b) => a.position - b.position)
      }
    })

    return [...datoPages]
  }

  const menuItems = menuTreeFromPages(allSitePage.nodes)
  const wecareMenuItems = false

  return (
    <>
      <HelmetDatoCms defer={false}>
        <meta name="format-detection" content="telephone=no" />
        <html lang="en" />
      </HelmetDatoCms>
      <div id="layout" className={style.layout}>
        <Headroom disableInlineStyles>
          <Header
            globalCtas={globalCtas}
            menuItems={menuItems}
            wecareMenuItems={wecareMenuItems}
            isWecareNowPage={false}
          />
        </Headroom>
        <SearchModal
          setIsModalOpen={setIsSearchModalOpen}
          isModalOpen={isSearchModalOpen}
        >
          <Search />
        </SearchModal>

        <div className={style.layout__content}>
          <main>{children}</main>
        </div>
        {(previewPopup || activePopup) && (
          <Popup
            popup={previewPopup || activePopup}
            onDismiss={previewPopup ? () => {} : handlePopupDismiss}
            onNeverShow={previewPopup ? () => {} : handlePopupNeverShow}
          />
        )}
        <Footer />
        {isSidebarOpen && (
          <div className={style.layout__sidebar}>
            <div className={style.layout__sidebar_buttons}>
              <div className={style.layout__sidebar_buttons_wrapper}>
                <HeaderButtons globalCtas={globalCtas} />
              </div>
            </div>

            <Drilldown
              isVisible={isSidebarOpen}
              items={menuItems}
              closeSidebar={closeSidebar}
              globalCtas={globalCtas}
            />
          </div>
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: toJsonLdHtml(schema) }}
        />
      </div>
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired
}

const HeaderButtons = ({ globalCtas }) => {
  return (
    <>
      <div className={style.header__buttons_desktop}>
        {globalCtas.callToActionButtons.map(cta => (
          <Button
            key={cta.id}
            size={'large'}
            icon={cta.icon ? cta.icon : null}
            to={cta?.link?.link?.path || cta?.link?.url}
            buttonStyle={cta.buttonStyle === 'primary' ? 'primary' : ''}
            color={cta.buttonStyle === 'primary' ? 'solid' : ''}
          >
            {cta?.link?.displayText}
          </Button>
        ))}
      </div>
    </>
  )
}

export default Layout
